import { provide } from '@lit/context'
import { HostAriaLabelMixin, SchmancyElement } from '@schmancy/mixins/index'
import { css, html, LitElement, type PropertyValues } from 'lit'
import { customElement, property, query, queryAssignedElements, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { when } from 'lit/directives/when.js'
import { magnetic } from '@schmancy/directives/magnetic'
import { sizeClasses } from '@schmancy/theme/control-size'
import { FocusHandoff } from '@schmancy/utils/focus-handoff'
import { SchmancyButtonSizeContext, type SchmancyButtonSize } from './context'
import './busy-edge'

export interface SchmancyButtonEventMap {
	SchmancyFocus: CustomEvent<void>
	SchmancyBlur: CustomEvent<void>
}

export type ButtonVariant = 'elevated' | 'filled' | 'filled tonal' | 'tonal' | 'outlined' | 'text' | 'plain'
export type ButtonColor = 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'neutral'

/** @csspart base - The underlying native `<button>` (or `<a>` when `href` is set). */
@customElement('schmancy-button')
export class SchmancyButton extends HostAriaLabelMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: inline-flex;
				width: fit-content;
				max-width: 100%;
				flex-shrink: 0;
				/* VISIBLE, and the focus ring is the whole reason. overflow:hidden here
				   clipped every descendant's paint to this box — including the
				   focus-visible outline the base draws at offset 2px, which lands
				   entirely OUTSIDE the border box and so survived nowhere. Measured
				   with a real Tab: 0 of 3,888 pixels changed on focus, on this
				   component and therefore on every one of the library's components
				   that mounts it (WCAG 2.4.7). schmancy-icon-button never carried this
				   declaration and its ring has always painted, which is the proof the
				   clip was the fault and not the offset.

				   The ripple and the state layer are not why it was here: both are
				   children of the base, and the base carries its own overflow-hidden
				   (see the classMap below), which is what actually clips them to the
				   rounded corner. This declaration only ever clipped one more thing
				   than it was asked to. An element's own outline is not clipped by its
				   own overflow, so the base keeps its clip and the ring escapes it. */
				overflow: visible;
				position: relative;
				touch-action: manipulation;
				border-radius: var(--schmancy-radius-xl, 0.875rem); /* mirrors --radius-xl (rounded-xl, lines ~266/345 below) */
				transition:
					box-shadow 300ms cubic-bezier(0.34, 1.56, 0.64, 1),
					transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1);
				/* Set to 1 by an overlay a MODAL overlay has covered, which is the only
				   case the sweep inerts — an anchored menu leaves its opener live. A
				   button's whole content is an action, so once inert it says nothing:
				   it cannot be pressed and it reports no value. That is what separates
				   it from a switch or a field, which still show their value covered and
				   so keep painting — the page behind a viewer stays readable, it just
				   stops offering what it cannot do. Unset reads 0. */
				opacity: calc(1 - var(--schmancy-overlay-covered, 0));
			}
			:host([width='full']) {
				display: flex;
				width: 100%;
			}
			:host(:state(busy)) {
				/* Busy controls stay focusable, so their text retains its readable
				   contrast. The progress cursor and running edge carry the busy state. */
				cursor: progress;
			}
			:host(:hover:not([disabled]):not([readonly]):not(:state(busy)):not([variant='plain'])) {
				box-shadow: 0 4px 16px -4px color-mix(in srgb, var(--schmancy-sys-color-primary-default) 20%, transparent);
			}
			:host(:active:not([disabled]):not([readonly]):not(:state(busy)):not([variant='plain'])) {
				transform: scale(0.97);
				box-shadow: 0 1px 4px -2px color-mix(in srgb, var(--schmancy-sys-color-primary-default) 10%, transparent);
				transition-duration: 100ms;
			}
			@media (prefers-reduced-motion: reduce) {
				:host {
					transition: none;
				}
				:host(:hover:not([disabled])) {
					box-shadow: none;
				}
				:host(:active:not([disabled])) {
					transform: none;
					box-shadow: none;
				}
			}
			:host *,
			* {
				touch-action: manipulation;
			}
		`,
	]

	protected static shadowRootOptions = {
		...LitElement.shadowRootOptions,
		mode: 'open',
		delegatesFocus: true,
	}

	static formAssociated = true
	private internals: ElementInternals | undefined
	/** Watches the enclosing <schmancy-form>'s aria-busy; feeds `formBusy` below. */
	private formBusyObserver?: MutationObserver

	constructor() {
		super()
		try {
			this.internals = this.attachInternals()
		} catch {
			this.internals = undefined
		}
		// Translates clicks into native form semantics for any <form>, not just <schmancy-form>.
		this.addEventListener('click', (e: MouseEvent) => {
			// Registered in the constructor, so it precedes every consumer's own
			// `@click` binding on this host and `stopImmediatePropagation` reaches
			// them — which is the half of `busy` that actually prevents the double
			// submit. A busy submit button inside <schmancy-form> was already
			// re-entrancy-guarded at the form; a bare action button had nothing.
			if (this.disabled || this.readonly || this.busy || this.formBusy) {
				e.preventDefault()
				e.stopImmediatePropagation()
				return
			}
			const form = this.internals?.form
			if (!form) return
			if (this.type === 'submit') {
				e.preventDefault()
				form.requestSubmit()
			} else if (this.type === 'reset') {
				e.preventDefault()
				form.reset()
			}
		})
	}

	get form(): HTMLFormElement | null {
		return this.internals?.form ?? null
	}

	formDisabledCallback(disabled: boolean): void {
		this.disabled = disabled
	}

	override connectedCallback(): void {
		super.connectedCallback()
		// schmancy-form sets aria-busy="true" while status === 'submitting'
		if (this.type !== 'submit') return
		const form = this.closest('schmancy-form') as HTMLElement | null
		if (!form) return
		// Feeds the SAME internal truth `busy` feeds, rather than writing the
		// treatment itself — a form submit and a bare action button reach one
		// expression in willUpdate() below, so the two cannot drift apart.
		const sync = () => {
			this.formBusy = form.getAttribute('aria-busy') === 'true'
		}
		sync()
		this.formBusyObserver = new MutationObserver(sync)
		this.formBusyObserver.observe(form, { attributes: true, attributeFilter: ['aria-busy'] })
	}

	override disconnectedCallback(): void {
		this.formBusyObserver?.disconnect()
		this.formBusyObserver = undefined
		super.disconnectedCallback()
	}

	/**
	 * The one writer of the busy treatment, for both of its inputs. `aria-busy`
	 * lands on the host (where it has always been readable, and where a test or
	 * automation probe looks) and on `[part="base"]` in render() — the host is a
	 * roleless generic node, so state written only there reaches no assistive
	 * technology, the same reason `aria-haspopup`/`aria-expanded` are forwarded.
	 *
	 * Two custom-state names, set and cleared in one expression so they cannot
	 * disagree: `busy` is the canonical one a consumer targets, and `submitting`
	 * is retained because it is what the form-submit treatment has always
	 * broadcast and what this library's docs told consumers to write CSS against.
	 */
	protected override willUpdate(changed: PropertyValues): void {
		super.willUpdate(changed)
		if (!changed.has('busy') && !changed.has('formBusy')) return
		const states = this.internals?.states
		if (this.busy || this.formBusy) {
			this.setAttribute('aria-busy', 'true')
			// Latched, never cleared: the edge's exit is a scaleX collapse toward the
			// trailing side, and an element removed the frame busy drops has nothing
			// left to play it on. A button that has never been busy renders no edge at
			// all, so an idle page carries none of this and no compositor layer with it.
			this.hasBeenBusy = true
			states?.add('busy')
			states?.add('submitting')
		} else {
			this.removeAttribute('aria-busy')
			states?.delete('busy')
			states?.delete('submitting')
		}
	}

	@query('[part="base"]', true)
	private nativeElement!: HTMLElement

	#ariaExpanded: 'true' | 'false' | null = null

	#ariaPressed: 'true' | 'false' | 'mixed' | null = null

	@property({ reflect: true, type: String })
	public variant: ButtonVariant = 'text'

	/** Falls back to a variant-derived default when unset: 'secondary' for tonal, else 'primary'. */
	@property({ reflect: true, type: String })
	public color?: ButtonColor

	@property({ reflect: true })
	public width: 'full' | 'auto' = 'auto'

	@property({ reflect: true })
	public justify: 'start' | 'center' = 'center'

	/** Provided via context so descendants (e.g. `<schmancy-icon>`) can auto-size against the enclosing button. */
	@provide({ context: SchmancyButtonSizeContext })
	@property({ type: String })
	public size: SchmancyButtonSize = 'md'

	@property({ reflect: true, type: String })
	public type: 'button' | 'reset' | 'submit' = 'button'

	/** Renders as an anchor when set. */
	@property()
	public href?: string

	/** With `href`, triggers a download with this filename instead of navigating. */
	@property()
	public download?: string

	@property()
	public target?: string

	@property({ type: Boolean, reflect: true })
	public disabled = false

	/**
	 * The button stands in for a value that cannot be changed here — a read-only
	 * field's trigger. Unlike `disabled` it stays focusable, named and at full ink,
	 * so the value is still read; what it gives up is every promise of an act: the
	 * pointer, the hover and press cues, the popup and expanded claims, a link
	 * base's `href`, and the activation itself, which it swallows. It announces as
	 * an ordinary button whose activation does nothing: ARIA gives `button` no
	 * `aria-readonly`, and `aria-disabled` would call a read-only value disabled.
	 */
	@property({ type: Boolean, reflect: true })
	public readonly = false

	/**
	 * The button's own action is in flight. NOT a synonym for `disabled`, and
	 * setting `disabled` instead is the mistake this property exists to end:
	 * `aria-disabled` tells a screen-reader user "you cannot", `aria-busy` tells
	 * them "wait", and a disabled control also drops out of the tab order, so a
	 * keyboard user loses their place mid-operation for the duration of a write
	 * they started (WCAG 2.2 AA). `busy` therefore leaves the control focusable
	 * and named, keeps its readable contrast, takes `cursor: progress`,
	 * swallows the click, and runs `schmancy-busy-edge` along its bottom edge.
	 *
	 * Nothing about the button's content moves: the label is never hidden and a
	 * prefix glyph stays exactly where the consumer put it. The edge is absolutely
	 * positioned and animates transform and opacity alone, so the box is identical
	 * busy and idle — the icon-less case (a plain "Continue") used to grow by the
	 * spinner rung plus the gap and shove its neighbours, which is the defect that
	 * removed the spinner from this state entirely.
	 *
	 * The two compose: `disabled` still means unavailable, `busy` means working.
	 * A control that is both renders disabled and swallows the click once.
	 *
	 * A `type="submit"` inside a `<schmancy-form>` gets the identical treatment
	 * with nothing set, from the form's own `aria-busy` — one internal truth, so
	 * the two surfaces cannot drift (see `willUpdate`).
	 */
	@property({ type: Boolean, reflect: true })
	public busy = false

	/** The enclosing form's submit state, the second input to the one busy truth. */
	@state()
	private formBusy = false

	/** Set the first time either input goes busy; keeps the edge mounted for its exit. */
	@state()
	private hasBeenBusy = false

	/**
	 * Renders icon-only: the box drops to the rung's square target and the label
	 * is clipped to the accessibility layer instead of removed, so the button
	 * still announces exactly what it announced with the label showing. That is
	 * the whole reason this is a mode of `schmancy-button` rather than a swap to
	 * `schmancy-icon-button` at the call site — a swap re-derives the name from
	 * the glyph, and "translate" is not what the control does.
	 *
	 * A field sets this on whatever sits in its `actions` slot, from the field's
	 * own width; a consumer may also set it directly.
	 *
	 * Ignored while the `prefix` slot is empty: with no glyph left behind, a
	 * collapsed button is a live, focusable, correctly-named control that paints
	 * nothing at all — a worse outcome than the label the collapse was saving
	 * room for.
	 */
	@property({ type: Boolean, reflect: true })
	public compact = false

	/**
	 * The `id` of the element focus moves to when this button is removed while
	 * it holds focus — the "Clear" whose own click empties the `when()` that
	 * renders it. Resolved in this button's tree scope, then each enclosing one;
	 * a non-focusable target hands on to its first focusable descendant. Unset,
	 * focus goes to what Shift+Tab would have reached, never to `<body>`.
	 */
	@property({ attribute: 'focus-successor' })
	public focusSuccessor?: string

	protected readonly focusHandoff = new FocusHandoff(this, () => this.focusSuccessor)

	/**
	 * ARIA state a trigger sets on this host — a popup relationship, its open
	 * state, an invalid field's ring. The host itself is a roleless generic node,
	 * so assistive technology reads none of these where the consumer writes them;
	 * they are only meaningful on `[part="base"]`, the node that actually carries
	 * the `button`/`link` role. Declared as reactive properties (not read off the
	 * host at render time) because a consumer's Lit attribute binding on this host
	 * schedules no update here on its own.
	 *
	 * Unset on every existing consumer, which renders exactly as before.
	 * `aria-describedby` is deliberately absent: an IDREF cannot cross a shadow
	 * boundary, so forwarding one would only point at an id this root has no copy
	 * of — a description belongs in the trigger's own accessible name instead.
	 */
	@property({ attribute: 'aria-haspopup' })
	public override ariaHasPopup: 'true' | 'false' | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog' | null = null

	public override set ariaExpanded(value: 'true' | 'false' | null) {
		const oldVal = this.#ariaExpanded
		this.#ariaExpanded = value
		// Off the HOST, onto the base. A roleless generic node carrying an ARIA
		// state attribute is a conformance failure in its own right (aria-expanded
		// is allowed only on a role that can be expanded), and the state is
		// meaningless here anyway — only [part="base"] carries the button role.
		if (this.hasAttribute('aria-expanded')) this.removeAttribute('aria-expanded')
		this.requestUpdate('ariaExpanded', oldVal)
	}

	@property({ attribute: 'aria-expanded' })
	public override get ariaExpanded() {
		return this.#ariaExpanded
	}

	/** A toggle button's own state — the face it is currently in, not the face it moves to. */
	public override set ariaPressed(value: 'true' | 'false' | 'mixed' | null) {
		const oldVal = this.#ariaPressed
		this.#ariaPressed = value
		if (this.hasAttribute('aria-pressed')) this.removeAttribute('aria-pressed')
		this.requestUpdate('ariaPressed', oldVal)
	}

	@property({ attribute: 'aria-pressed' })
	public override get ariaPressed() {
		return this.#ariaPressed
	}

	/**
	 * The region this button governs, passed as the ELEMENT rather than as an id.
	 *
	 * An `aria-controls` IDREF cannot cross a shadow boundary — the reason
	 * `aria-describedby` is absent above — and the region a disclosure opens
	 * almost always lives in the CONSUMER's root while this base lives in this
	 * one, so an id forwarded here would name something this root has no copy of.
	 * ARIA element reflection takes the reference instead and resolves across
	 * roots, so `.controls=${this.region}` reaches what an attribute cannot.
	 *
	 * Applied only where the platform implements the reflection; `aria-expanded`
	 * carries the state either way, and the state is what a reader needs.
	 */
	@property({ attribute: false })
	public controls: Element | null = null

	@property({ attribute: 'aria-invalid' })
	public override ariaInvalid: 'true' | 'false' | 'grammar' | 'spelling' | null = null

	/** The shortcut that also presses this button, in ARIA's own grammar (`Meta+Enter`). A visible hint for it belongs in `suffix` as an `aria-hidden` `<schmancy-kbd>`. */
	@property({ attribute: 'aria-keyshortcuts' })
	public override ariaKeyShortcuts: string | null = null

	@queryAssignedElements({
		slot: 'prefix',
		flatten: true,
		selector: 'img',
	})
	private prefixImgs!: HTMLImageElement[]

	@queryAssignedElements({ slot: 'prefix', flatten: true })
	private prefixContent!: HTMLElement[]

	@state()
	private hasPrefix = false

	/** Bound as a class field so the `@slotchange` binding keeps one reference across renders. */
	private syncPrefix = () => {
		this.hasPrefix = this.prefixContent.length > 0
	}

	@queryAssignedElements({
		slot: 'suffix',
		flatten: true,
		selector: 'img',
	})
	private suffixImgs!: HTMLImageElement[]

	public override focus(options?: FocusOptions) {
		if (!this.hasUpdated) {
			void this.updateComplete.then(() => {
				if (this.isConnected) this.nativeElement.focus(options)
			})
			return
		}
		this.nativeElement.focus(options)
	}

	public override blur() {
		this.nativeElement.blur()
	}

	protected get effectiveColor(): ButtonColor {
		if (this.color) return this.color

		const effectiveVariant = this.variant === 'tonal' ? 'filled tonal' : this.variant
		return effectiveVariant === 'filled tonal' ? 'secondary' : 'primary'
	}

	protected get imgClasses(): string[] {
		const sizeMap = {
			xxs: 'w-3 h-3', // 12px for 24px button (ultra-compact)
			xs: 'w-4 h-4', // 16px for 32px button (M3 dense)
			sm: 'w-5 h-5', // 20px for 40px button (M3 default)
			md: 'w-6 h-6', // 24px for 48px button (M3 large)
			lg: 'w-7 h-7', // 28px for 56px button (M3 extra large)
		}
		return [sizeMap[this.size], 'object-contain']
	}

	/**
	 * The controlled-region reference, re-applied on every render because the base
	 * is re-created by the href branch and a property set on the old one is gone.
	 * Guarded on the platform implementing the reflection: where it does not, the
	 * relationship is simply absent and `aria-expanded` still carries the state.
	 */
	protected updated() {
		const base = this.nativeElement as (HTMLElement & { ariaControlsElements?: readonly Element[] | null }) | null
		if (!base || !('ariaControlsElements' in base)) return
		base.ariaControlsElements = this.controls ? [this.controls] : null
	}

	firstUpdated() {
		this.prefixImgs?.forEach(img => {
			img.classList.add(...this.imgClasses)
			if (!img.hasAttribute('alt')) {
				img.setAttribute('alt', '')
			}
		})
		this.suffixImgs?.forEach(img => {
			img.classList.add(...this.imgClasses)
			if (!img.hasAttribute('alt')) {
				img.setAttribute('alt', '')
			}
		})
	}

	click(): void {
		this.dispatchEvent(new Event('click', { bubbles: true, composed: true }))
	}

	private preventDefault(event: Event) {
		event.preventDefault()
		event.stopPropagation()
	}

	render() {
		// backwards compat: 'tonal' variant maps to 'filled tonal'
		const effectiveVariant = this.variant === 'tonal' ? 'filled tonal' : this.variant

		const busy = this.busy || this.formBusy

		// The filled grounds are the brand fill, where the edge has less contrast to
		// work with than it does against a dark surface; boost raises the alpha of
		// every band inside its own 3→4px height, and touches nothing outside it.
		const boostEdge = effectiveVariant === 'filled' || effectiveVariant === 'filled tonal'

		const collapsed = this.compact && this.hasPrefix

		const classes = {
			// sizeClasses provides: min-h-control-<size> (height floor), text-<size>
			// (font+line-height), px-pad-button-<size> (horizontal pad), gap-<n> (gap).
			// Vertical padding kept below; font-medium hoisted here.
			//
			// 'floor', not the default 'exact': a button's label is prose the caller
			// writes and a narrow container wraps it onto a second line the rung has
			// no room for — with an exact height that line was clipped mid-glyph.
			// Single-line geometry is unchanged, because at every size the rung is
			// >= label leading (or icon glyph) plus this element's own py-*.
			//
			// `'square'` swaps the role's horizontal padding for the rung's width:
			// a collapsed button has no label to size against, and padding alone
			// leaves it as narrow as its glyph. `flex-1` comes off with it, because
			// a flex item's `flex-basis: 0` outranks `width` on the main axis and
			// would resolve that width to nothing.
			//
			// `'none'` on both axes is what 'plain' asks for: it keeps the type
			// scale and the gap — it still reads at the rung's font size — and takes
			// neither the height floor nor any horizontal box, because it sits
			// inline at its neighbour's baseline rather than inside a control box.
			//
			// `collapsed` outranks 'plain' on BOTH axes, because that premise is
			// exactly what a collapsed button is not: its label is in `sr-only` and
			// a glyph is all that is left, so it is a control box or it is nothing
			// — and `flex-1`/`w-full` come off it below, leaving the base to resolve
			// at its glyph's own intrinsic width if the rung's square comes off too.
			// The square is a target-size floor, not box decoration. 'plain' shipped
			// promising no padding and no height floor; it never promised away the
			// icon-only square, and this is not a path a consumer has to type
			// `compact` to reach — `field-actions.ts` toggles that attribute onto
			// every control in a field's `actions` slot below its width threshold.
			//
			// Every token this ladder owns is emitted ONCE, here, under the
			// condition that decides it, because a later key CANNOT take one back:
			// `SchmancyElement.classMap` splits a multi-token key into single-token
			// entries and ORs them (its own comment says why assigning was worse), so
			// a token is on when any key naming it is on. The three `!== 'plain'`
			// keys that used to sit below this one subtracted nothing — measured on a
			// live `variant="plain"`, the base carried `min-h-control-md` and
			// `px-pad-button-md` beside `p-0`, and since Tailwind orders the
			// padding-inline longhand after the padding shorthand, plain rendered the
			// rung's 24px of inline padding and its 48px floor either way.
			[sizeClasses(
				this.size,
				'button',
				effectiveVariant === 'plain' && !collapsed ? 'none' : 'floor',
				effectiveVariant === 'plain' && !collapsed ? 'none' : collapsed ? 'square' : 'role',
			)]: true,
			// plain's only padding declaration, on both axes: the ladder emits no
			// px-pad-* for it above, and the py-* rungs below are all gated off it.
			'p-0': effectiveVariant === 'plain',
			// overflow-hidden here stays: it clips the absolutely-positioned state
			// layer and the ripple, both children of this base, to the rounded corner.
			// It was never what cut the label — a box that grows leaves it nothing to
			// cut — and it does not clip this element's OWN outline, which is why the
			// focus ring paints now that :host no longer clips it (see :host above).
			'z-0 transition-all duration-200 motion-reduce:transition-none relative rounded-xl flex items-center outline-secondary-default focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 outline-hidden overflow-hidden': true,
			'flex-1': !collapsed,
			// The base's measure comes from the host rather than from its own content,
			// and it has to be spelled: this element is a <button>/<a>, whose `width:
			// auto` is shrink-to-fit and therefore clamps at its own min-content — the
			// longest unbreakable token the slotted content holds — however narrow the
			// host is. A host handed a definite measure by a class or by its container
			// then rendered a base WIDER than itself and `:host { overflow: hidden }`
			// ate the difference in silence: a 159-char JSON preview in a 420px button
			// laid out at 538px, losing the end of every line and the button's own right
			// border with it. With the host's measure to lay out against, an
			// `overflow-wrap` the content declares finally has a box to break in.
			//
			// A `min-w-0` beside this is inert, and was measured so: a flex item's
			// automatic minimum takes the smaller of its content and specified size
			// suggestions, and this percentage is the specified one.
			//
			// It costs a host that fits its content nothing. `:host` is
			// `width: fit-content`, resolved from this element's max-content
			// contribution — which treats a percentage width as auto — so the base is
			// asked for exactly the measure the host resolved from it. Measured identical
			// across every size, variant and slot shape; only the case that was
			// overflowing its own host moved.
			'w-full': !collapsed,
			// text-* rides with justify-* so one property owns the inline axis on both bases. It is a
			// no-op on the <button> base (UA already sets text-align:center) but load-bearing on the
			// <a> base, which has none and would otherwise start-align a wrapped label's lines.
			'justify-center text-center': collapsed || this.justify !== 'start',
			'justify-start text-left': !collapsed && this.justify === 'start',
			// vertical padding — not covered by sizeClasses (horizontal-only px-pad-*).
			// 'plain' carries none — the p-0 above is its whole padding, this axis
			// included.
			'py-1': this.size === 'xxs' && effectiveVariant !== 'plain',
			'py-2': this.size === 'xs' && effectiveVariant !== 'plain',
			'py-2.5': this.size === 'sm' && effectiveVariant !== 'plain',
			'py-3': this.size === 'md' && effectiveVariant !== 'plain',
			'py-4': this.size === 'lg' && effectiveVariant !== 'plain',
			'font-medium': true,
			'tracking-[0.1px]': true,
			'cursor-pointer': !this.disabled && !busy && !this.readonly,
			// Spelled rather than left to the UA: an <a href> base defaults to a pointer.
			'cursor-default': this.readonly && !this.disabled && !busy,
			// The :host rule's `cursor: progress` never reached the pointer on its
			// own: the pointer is over THIS element for the whole box, and its own
			// `cursor-pointer` wins. The progress cursor has to be declared here.
			'cursor-progress': !this.disabled && busy,
			'opacity-[var(--schmancy-sys-state-opacity-disabled,0.38)]': this.disabled,
			// 'plain' carries no state-layer wash div at all (see below) — a filled
			// rectangle around zero-padding text clips the glyphs instead of reading
			// as a hover cue. The cue lives directly on the text: an underline on
			// hover, a dip on press. Focus stays visible through the outline-* set
			// on this same element, unconditional across every variant.
			'hover:underline underline-offset-2': !this.disabled && !this.readonly && effectiveVariant === 'plain',
			'active:opacity-70': !this.disabled && !this.readonly && effectiveVariant === 'plain',
			'hover:shadow-sm': !this.disabled && !this.readonly && effectiveVariant === 'elevated',
			'bg-surface-low shadow-xs': effectiveVariant === 'elevated',
			'bg-transparent border-1 border-solid': effectiveVariant === 'outlined',
			// Neutral outlined controls share the resting frame role with form fields.
			'border-control-border':
				effectiveVariant === 'outlined' && (this.effectiveColor === 'primary' || this.effectiveColor === 'neutral'),
			'border-success-default': effectiveVariant === 'outlined' && this.effectiveColor === 'success',
			'border-error-default': effectiveVariant === 'outlined' && this.effectiveColor === 'error',
			'border-warning-default': effectiveVariant === 'outlined' && this.effectiveColor === 'warning',
			'border-info-default': effectiveVariant === 'outlined' && this.effectiveColor === 'info',
			'border-secondary-default': effectiveVariant === 'outlined' && this.effectiveColor === 'secondary',
			'bg-primary-default': effectiveVariant === 'filled' && this.effectiveColor === 'primary',
			'bg-secondary-default': effectiveVariant === 'filled' && this.effectiveColor === 'secondary',
			// The state roles fill from `pinned` — the scheme-stable pair whose
			// measurements live on `errorPinned` in theme.format.ts.
			'bg-success-pinned': effectiveVariant === 'filled' && this.effectiveColor === 'success',
			'bg-error-pinned': effectiveVariant === 'filled' && this.effectiveColor === 'error',
			'bg-warning-pinned': effectiveVariant === 'filled' && this.effectiveColor === 'warning',
			'bg-info-pinned': effectiveVariant === 'filled' && this.effectiveColor === 'info',
			'bg-surface-containerHighest': effectiveVariant === 'filled' && this.effectiveColor === 'neutral',
			'text-primary-on': effectiveVariant === 'filled' && this.effectiveColor === 'primary',
			'text-secondary-on': effectiveVariant === 'filled' && this.effectiveColor === 'secondary',
			'text-success-on-pinned': effectiveVariant === 'filled' && this.effectiveColor === 'success',
			'text-error-on-pinned': effectiveVariant === 'filled' && this.effectiveColor === 'error',
			'text-warning-on-pinned': effectiveVariant === 'filled' && this.effectiveColor === 'warning',
			'text-info-on-pinned': effectiveVariant === 'filled' && this.effectiveColor === 'info',
			'text-surface-on': effectiveVariant === 'filled' && this.effectiveColor === 'neutral',
			// The boundary that ground owes, drawn from the role's own `default`.
			// Inset, so it reads as this button's own edge rather than as a second
			// ring competing with the focus ring that now paints outside the box.
			'ring-1 ring-inset':
				effectiveVariant === 'filled' &&
				(this.effectiveColor === 'success' ||
					this.effectiveColor === 'error' ||
					this.effectiveColor === 'warning' ||
					this.effectiveColor === 'info'),
			'ring-success-default': effectiveVariant === 'filled' && this.effectiveColor === 'success',
			'ring-error-default': effectiveVariant === 'filled' && this.effectiveColor === 'error',
			'ring-warning-default': effectiveVariant === 'filled' && this.effectiveColor === 'warning',
			'ring-info-default': effectiveVariant === 'filled' && this.effectiveColor === 'info',
			'bg-primary-container': effectiveVariant === 'filled tonal' && this.effectiveColor === 'primary',
			'bg-secondary-container': effectiveVariant === 'filled tonal' && this.effectiveColor === 'secondary',
			'bg-success-container': effectiveVariant === 'filled tonal' && this.effectiveColor === 'success',
			'bg-error-container': effectiveVariant === 'filled tonal' && this.effectiveColor === 'error',
			'bg-warning-container': effectiveVariant === 'filled tonal' && this.effectiveColor === 'warning',
			'bg-info-container': effectiveVariant === 'filled tonal' && this.effectiveColor === 'info',
			'bg-surface-containerLow': effectiveVariant === 'filled tonal' && this.effectiveColor === 'neutral',
			'text-primary-onContainer': effectiveVariant === 'filled tonal' && this.effectiveColor === 'primary',
			'text-secondary-onContainer': effectiveVariant === 'filled tonal' && this.effectiveColor === 'secondary',
			'text-success-onContainer': effectiveVariant === 'filled tonal' && this.effectiveColor === 'success',
			'text-error-onContainer': effectiveVariant === 'filled tonal' && this.effectiveColor === 'error',
			'text-warning-onContainer': effectiveVariant === 'filled tonal' && this.effectiveColor === 'warning',
			'text-info-onContainer': effectiveVariant === 'filled tonal' && this.effectiveColor === 'info',
			'text-primary-default':
				(effectiveVariant === 'text' ||
					effectiveVariant === 'plain' ||
					effectiveVariant === 'elevated' ||
					effectiveVariant === 'outlined') &&
				this.effectiveColor === 'primary',
			'text-secondary-default':
				(effectiveVariant === 'text' ||
					effectiveVariant === 'plain' ||
					effectiveVariant === 'elevated' ||
					effectiveVariant === 'outlined') &&
				this.effectiveColor === 'secondary',
			'text-success-default':
				(effectiveVariant === 'text' ||
					effectiveVariant === 'plain' ||
					effectiveVariant === 'elevated' ||
					effectiveVariant === 'outlined') &&
				this.effectiveColor === 'success',
			'text-error-default':
				(effectiveVariant === 'text' ||
					effectiveVariant === 'plain' ||
					effectiveVariant === 'elevated' ||
					effectiveVariant === 'outlined') &&
				this.effectiveColor === 'error',
			'text-warning-default':
				(effectiveVariant === 'text' ||
					effectiveVariant === 'plain' ||
					effectiveVariant === 'elevated' ||
					effectiveVariant === 'outlined') &&
				this.effectiveColor === 'warning',
			'text-info-default':
				(effectiveVariant === 'text' ||
					effectiveVariant === 'plain' ||
					effectiveVariant === 'elevated' ||
					effectiveVariant === 'outlined') &&
				this.effectiveColor === 'info',
			'text-surface-onVariant':
				(effectiveVariant === 'text' ||
					effectiveVariant === 'plain' ||
					effectiveVariant === 'elevated' ||
					effectiveVariant === 'outlined' ||
					effectiveVariant === 'filled tonal') &&
				this.effectiveColor === 'neutral',
		}

		// 'plain' skips this div entirely (see the render() gate below) — it hugs
		// bare, zero-padding text, and any filled wash of any corner radius reads
		// as a smear clipping the glyphs rather than a hover cue. Its own
		// hover/active/focus treatment lives on the base element's own classes
		// instead (underline + opacity dip), among the color classes above.
		const stateLayerClasses = {
			'absolute inset-0 hover:opacity-[var(--schmancy-sys-state-opacity-hover,0.08)] z-0 rounded-xl': true,
			'focus-visible:opacity-[var(--schmancy-sys-state-opacity-focus,0.12)]': true,
			'active:opacity-[var(--schmancy-sys-state-opacity-pressed,0.12)]': true,
			'hover:bg-primary-on': effectiveVariant === 'filled' && this.effectiveColor === 'primary',
			'hover:bg-secondary-on': effectiveVariant === 'filled' && this.effectiveColor === 'secondary',
			// The wash rides the ink that sits ON the fill, so it follows the fill to
			// `pinned`: `<role>-on` is a dark tone in dark scheme and would wash a
			// dark ground with dark, collapsing hover/focus/pressed to 1.05:1.
			'hover:bg-success-on-pinned': effectiveVariant === 'filled' && this.effectiveColor === 'success',
			'hover:bg-error-on-pinned': effectiveVariant === 'filled' && this.effectiveColor === 'error',
			'hover:bg-warning-on-pinned': effectiveVariant === 'filled' && this.effectiveColor === 'warning',
			'hover:bg-info-on-pinned': effectiveVariant === 'filled' && this.effectiveColor === 'info',
			'hover:bg-surface-on': effectiveVariant === 'filled' && this.effectiveColor === 'neutral',
			'hover:bg-primary-default':
				(effectiveVariant === 'outlined' || effectiveVariant === 'elevated' || effectiveVariant === 'text') &&
				this.effectiveColor === 'primary',
			'hover:bg-secondary-default':
				(effectiveVariant === 'outlined' || effectiveVariant === 'elevated' || effectiveVariant === 'text') &&
				this.effectiveColor === 'secondary',
			'hover:bg-success-default':
				(effectiveVariant === 'outlined' || effectiveVariant === 'elevated' || effectiveVariant === 'text') &&
				this.effectiveColor === 'success',
			'hover:bg-error-default':
				(effectiveVariant === 'outlined' || effectiveVariant === 'elevated' || effectiveVariant === 'text') &&
				this.effectiveColor === 'error',
			'hover:bg-warning-default':
				(effectiveVariant === 'outlined' || effectiveVariant === 'elevated' || effectiveVariant === 'text') &&
				this.effectiveColor === 'warning',
			'hover:bg-info-default':
				(effectiveVariant === 'outlined' || effectiveVariant === 'elevated' || effectiveVariant === 'text') &&
				this.effectiveColor === 'info',
			'hover:bg-surface-onVariant':
				(effectiveVariant === 'outlined' || effectiveVariant === 'elevated' || effectiveVariant === 'text') &&
				this.effectiveColor === 'neutral',
			'hover:bg-primary-container': effectiveVariant === 'filled tonal' && this.effectiveColor === 'primary',
			'hover:bg-secondary-container': effectiveVariant === 'filled tonal' && this.effectiveColor === 'secondary',
			'hover:bg-success-container': effectiveVariant === 'filled tonal' && this.effectiveColor === 'success',
			'hover:bg-error-container': effectiveVariant === 'filled tonal' && this.effectiveColor === 'error',
			'hover:bg-warning-container': effectiveVariant === 'filled tonal' && this.effectiveColor === 'warning',
			'hover:bg-info-container': effectiveVariant === 'filled tonal' && this.effectiveColor === 'info',
			'hover:bg-surface-containerLow': effectiveVariant === 'filled tonal' && this.effectiveColor === 'neutral',
		}

		if (this.href) {
			return html`
				<a
					${magnetic({ strength: this.disabled || this.readonly || busy ? 0 : 3, radius: 60 })}
					part="base"
					href=${ifDefined(this.disabled || this.readonly || busy ? undefined : this.href)}
					role=${ifDefined(!this.disabled && (this.readonly || busy) ? 'link' : undefined)}
					download=${ifDefined(this.download)}
					target=${ifDefined(this.target)}
					aria-label=${ifDefined(this.ariaLabel || undefined)}
					aria-haspopup=${ifDefined(this.readonly ? undefined : (this.ariaHasPopup ?? undefined))}
					aria-expanded=${ifDefined(this.readonly ? undefined : (this.ariaExpanded ?? undefined))}
					aria-pressed=${ifDefined(this.ariaPressed ?? undefined)}
					aria-invalid=${ifDefined(this.ariaInvalid ?? undefined)}
					aria-keyshortcuts=${ifDefined(this.ariaKeyShortcuts ?? undefined)}
					aria-busy=${ifDefined(busy ? 'true' : undefined)}
					class="${this.classMap(classes)}"
					tabindex=${this.disabled ? '-1' : '0'}
					aria-disabled=${this.disabled}
					@click=${this.disabled || busy ? this.preventDefault : undefined}
				>
					${when(!this.disabled && !busy && !this.readonly && effectiveVariant !== 'plain', () => html`<div class="${this.classMap(stateLayerClasses)}"></div>`)}
					${when(this.hasBeenBusy, () => html`<schmancy-busy-edge ?running=${busy} ?boost=${boostEdge}></schmancy-busy-edge>`)}
					<slot name="prefix" @slotchange=${this.syncPrefix}></slot>
					${when(
						collapsed,
						() => html`<span class="sr-only"><slot></slot></span>`,
						() => html`<slot></slot>`,
					)}
					<slot name="suffix"></slot>
				</a>
			`
		}

		return html`
			<button
				${magnetic({ strength: this.disabled || this.readonly || busy ? 0 : 3, radius: 60 })}
				part="base"
				aria-label=${ifDefined(this.ariaLabel || undefined)}
				aria-haspopup=${ifDefined(this.readonly ? undefined : (this.ariaHasPopup ?? undefined))}
				aria-expanded=${ifDefined(this.readonly ? undefined : (this.ariaExpanded ?? undefined))}
				aria-pressed=${ifDefined(this.ariaPressed ?? undefined)}
				aria-invalid=${ifDefined(this.ariaInvalid ?? undefined)}
				aria-keyshortcuts=${ifDefined(this.ariaKeyShortcuts ?? undefined)}
				aria-busy=${ifDefined(busy ? 'true' : undefined)}
				?disabled=${this.disabled}
				class="${this.classMap(classes)}"
				type=${ifDefined(this.type)}
				tabindex=${ifDefined(this.disabled ? '-1' : undefined)}
			>
				${when(!this.disabled && !busy && !this.readonly && effectiveVariant !== 'plain', () => html`<div class="${this.classMap(stateLayerClasses)}"></div>`)}
				${when(this.hasBeenBusy, () => html`<schmancy-busy-edge ?running=${busy} ?boost=${boostEdge}></schmancy-busy-edge>`)}
				<slot name="prefix" @slotchange=${this.syncPrefix}></slot>
				${when(
					collapsed,
					() => html`<span class="sr-only"><slot></slot></span>`,
					() => html`<slot></slot>`,
				)}
				<slot name="suffix"></slot>
			</button>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-button': SchmancyButton
	}
}
